
import { isTokenExpired, getTimeUntilExpiration } from './tokenUtils';

export interface SessionConfig {
  sessionDuration: number; // in milliseconds
  inactivityTimeout: number; // in milliseconds
  warningBeforeExpiration: number; // in milliseconds
}

const DEFAULT_CONFIG: SessionConfig = {
  sessionDuration: 24 * 60 * 60 * 1000, // 24 hours
  inactivityTimeout: 2 * 60 * 60 * 1000, // 2 hours
  warningBeforeExpiration: 5 * 60 * 1000, // 5 minutes
};

export type SessionEventType = 'warning' | 'expired' | 'inactivity';

export class SessionManager {
  private static instance: SessionManager;
  private config: SessionConfig;
  private lastActivityTime: number;
  private eventListeners = new Map<SessionEventType, Set<Function>>();
  private warningShown = false;
  private activityCheckInterval?: number;
  private expirationCheckInterval?: number;

  private constructor(config?: Partial<SessionConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.lastActivityTime = Date.now();
    this.setupActivityListeners();
    this.startSessionMonitoring();
  }

  public static getInstance(config?: Partial<SessionConfig>): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager(config);
    }
    return SessionManager.instance;
  }

  private setupActivityListeners(): void {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const updateActivity = () => this.updateActivity();
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
  }

  private updateActivity(): void {
    this.lastActivityTime = Date.now();
  }

  private startSessionMonitoring(): void {
    // Check for inactivity every minute
    this.activityCheckInterval = window.setInterval(() => {
      this.checkInactivity();
    }, 60000);

    // Check for token expiration every 30 seconds
    this.expirationCheckInterval = window.setInterval(() => {
      this.checkTokenExpiration();
    }, 30000);
  }

  private checkInactivity(): void {
    const inactiveTime = Date.now() - this.lastActivityTime;
    
    if (inactiveTime >= this.config.inactivityTimeout) {
      console.log('🕐 Session expired due to inactivity');
      this.emit('inactivity');
    }
  }

  private checkTokenExpiration(): void {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (isTokenExpired(token)) {
      console.log('🕐 Token has expired');
      this.emit('expired');
      return;
    }

    const timeUntilExpiration = getTimeUntilExpiration(token);
    if (timeUntilExpiration !== null && timeUntilExpiration <= this.config.warningBeforeExpiration) {
      if (!this.warningShown) {
        console.log('⚠️ Token expiring soon:', timeUntilExpiration / 1000, 'seconds');
        this.emit('warning', { timeRemaining: timeUntilExpiration });
        this.warningShown = true;
      }
    } else {
      this.warningShown = false;
    }
  }

  public addEventListener(event: SessionEventType, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
  }

  public removeEventListener(event: SessionEventType, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  private emit(event: SessionEventType, data?: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in session event listener for ${event}:`, error);
        }
      });
    }
  }

  public isSessionValid(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;

    if (isTokenExpired(token)) return false;

    const inactiveTime = Date.now() - this.lastActivityTime;
    if (inactiveTime >= this.config.inactivityTimeout) return false;

    return true;
  }

  public getSessionInfo() {
    const token = localStorage.getItem('token');
    const timeUntilExpiration = token ? getTimeUntilExpiration(token) : null;
    const inactiveTime = Date.now() - this.lastActivityTime;

    return {
      hasToken: !!token,
      isExpired: token ? isTokenExpired(token) : true,
      timeUntilExpiration,
      inactiveTime,
      isValid: this.isSessionValid(),
    };
  }

  public cleanup(): void {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
    }
    if (this.expirationCheckInterval) {
      clearInterval(this.expirationCheckInterval);
    }
  }
}

export const sessionManager = SessionManager.getInstance();
